import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/apiClient';
import './MetricCharts.css';

const MetricCharts = () => {
  const [trends, setTrends] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    agent_name: '',
    start_date: '',
    end_date: '',
    group_by: 'day',
    metric: 'accuracy'
  });

  useEffect(() => {
    fetchChartData();
  }, [filters]);

  const fetchChartData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch trends
      const trendsParams = new URLSearchParams();
      if (filters.agent_name) trendsParams.append('agent_name', filters.agent_name);
      if (filters.start_date) trendsParams.append('start_date', filters.start_date);
      if (filters.end_date) trendsParams.append('end_date', filters.end_date);
      trendsParams.append('group_by', filters.group_by);
      trendsParams.append('metric', filters.metric);

      const trendsResponse = await apiFetch(`/api/evals/trends?${trendsParams}`);
      if (trendsResponse.ok) {
        const trendsData = await trendsResponse.json();
        setTrends(trendsData.trends || []);
      }

      // Fetch metrics for comparison
      const metricsParams = new URLSearchParams();
      if (filters.agent_name) metricsParams.append('agent_name', filters.agent_name);

      const metricsResponse = await apiFetch(`/api/evals/metrics?${metricsParams}`);
      if (metricsResponse.ok) {
        const metricsData = await metricsResponse.json();
        setMetrics(metricsData.metrics || {});
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError('Failed to load chart data');
      setLoading(false);
    }
  };

  const renderTimeSeriesChart = () => {
    if (trends.length === 0) {
      return <div className="chart-empty">No trend data available</div>;
    }

    const maxValue = Math.max(...trends.map(t => t.accuracy || 0), 1);
    const chartHeight = 300;
    const chartWidth = 800;
    const padding = 40;
    const innerWidth = chartWidth - 2 * padding;
    const innerHeight = chartHeight - 2 * padding;

    const points = trends.map((trend, index) => {
      const x = padding + (index / (trends.length - 1 || 1)) * innerWidth;
      const y = padding + innerHeight - (trend.accuracy / maxValue) * innerHeight;
      return { x, y, trend };
    });

    const pathData = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    return (
      <div className="chart-container">
        <h3>Accuracy Trends Over Time</h3>
        <svg width={chartWidth} height={chartHeight} className="chart-svg">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + innerHeight - ratio * innerHeight;
            const value = (ratio * maxValue * 100).toFixed(0);
            return (
              <g key={ratio}>
                <line
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="#e0e0e0"
                  strokeWidth="1"
                />
                <text x={padding - 10} y={y + 5} fontSize="12" fill="#666">
                  {value}%
                </text>
              </g>
            );
          })}

          {/* Trend line */}
          <path
            d={pathData}
            fill="none"
            stroke="#007bff"
            strokeWidth="2"
          />

          {/* Data points */}
          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="4"
                fill="#007bff"
                className="chart-point"
              />
              <title>
                Date: {point.trend.date}
                Accuracy: {(point.trend.accuracy * 100).toFixed(1)}%
              </title>
            </g>
          ))}

          {/* X-axis labels */}
          {trends.map((trend, index) => {
            if (index % Math.ceil(trends.length / 5) === 0 || index === trends.length - 1) {
              const x = padding + (index / (trends.length - 1 || 1)) * innerWidth;
              return (
                <text
                  key={index}
                  x={x}
                  y={chartHeight - padding + 20}
                  fontSize="10"
                  fill="#666"
                  textAnchor="middle"
                >
                  {trend.date}
                </text>
              );
            }
            return null;
          })}
        </svg>
      </div>
    );
  };

  const renderComparisonChart = () => {
    const agents = ['extractor', 'evaluator', 'summarizer', 'formatter', 'explainability'];
    const agentData = agents.map(agent => {
      // Fetch agent-specific metrics if available
      return {
        agent: agent.charAt(0).toUpperCase() + agent.slice(1),
        accuracy: 0.85 // Placeholder - would come from API
      };
    });

    const maxValue = Math.max(...agentData.map(d => d.accuracy), 1);
    const chartHeight = 300;
    const barWidth = 60;
    const spacing = 20;
    const chartWidth = agents.length * (barWidth + spacing) + 40;

    return (
      <div className="chart-container">
        <h3>Agent Comparison</h3>
        <svg width={chartWidth} height={chartHeight} className="chart-svg">
          {agentData.map((data, index) => {
            const x = 40 + index * (barWidth + spacing);
            const barHeight = (data.accuracy / maxValue) * (chartHeight - 80);
            const y = chartHeight - 40 - barHeight;

            return (
              <g key={data.agent}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill="#007bff"
                  className="chart-bar"
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 5}
                  fontSize="12"
                  fill="#333"
                  textAnchor="middle"
                  fontWeight="600"
                >
                  {(data.accuracy * 100).toFixed(1)}%
                </text>
                <text
                  x={x + barWidth / 2}
                  y={chartHeight - 20}
                  fontSize="11"
                  fill="#666"
                  textAnchor="middle"
                >
                  {data.agent}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  const renderDistributionChart = () => {
    // Simple distribution visualization
    const buckets = [0, 0.2, 0.4, 0.6, 0.8, 1.0];
    const distribution = buckets.map((bucket, index) => {
      const nextBucket = buckets[index + 1] || 1.0;
      // Placeholder data - would come from actual results
      const count = Math.floor(Math.random() * 20) + 5;
      return { range: `${(bucket * 100).toFixed(0)}-${(nextBucket * 100).toFixed(0)}%`, count };
    });

    const maxCount = Math.max(...distribution.map(d => d.count), 1);
    const chartHeight = 300;
    const barWidth = 80;
    const spacing = 15;
    const chartWidth = distribution.length * (barWidth + spacing) + 40;

    return (
      <div className="chart-container">
        <h3>Accuracy Distribution</h3>
        <svg width={chartWidth} height={chartHeight} className="chart-svg">
          {distribution.map((data, index) => {
            const x = 40 + index * (barWidth + spacing);
            const barHeight = (data.count / maxCount) * (chartHeight - 80);
            const y = chartHeight - 40 - barHeight;

            return (
              <g key={data.range}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill="#28a745"
                  className="chart-bar"
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 5}
                  fontSize="11"
                  fill="#333"
                  textAnchor="middle"
                  fontWeight="600"
                >
                  {data.count}
                </text>
                <text
                  x={x + barWidth / 2}
                  y={chartHeight - 20}
                  fontSize="10"
                  fill="#666"
                  textAnchor="middle"
                >
                  {data.range}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="metric-charts">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading chart data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metric-charts">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchChartData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="metric-charts">
      <div className="charts-header">
        <h1>Evaluation Metrics Charts</h1>
        <button onClick={fetchChartData} className="btn-secondary">
          Refresh
        </button>
      </div>

      <div className="chart-controls">
        <div className="control-group">
          <label>Agent:</label>
          <select
            value={filters.agent_name}
            onChange={(e) => setFilters(prev => ({ ...prev, agent_name: e.target.value }))}
          >
            <option value="">All Agents</option>
            <option value="extractor">Extractor</option>
            <option value="evaluator">Evaluator</option>
            <option value="summarizer">Summarizer</option>
            <option value="formatter">Formatter</option>
            <option value="explainability">Explainability</option>
          </select>
        </div>

        <div className="control-group">
          <label>Group By:</label>
          <select
            value={filters.group_by}
            onChange={(e) => setFilters(prev => ({ ...prev, group_by: e.target.value }))}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>

        <div className="control-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
          />
        </div>

        <div className="control-group">
          <label>End Date:</label>
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
          />
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-section">
          {renderTimeSeriesChart()}
        </div>

        <div className="chart-section">
          {renderComparisonChart()}
        </div>

        <div className="chart-section">
          {renderDistributionChart()}
        </div>
      </div>
    </div>
  );
};

export default MetricCharts;



